import React, { useState, useEffect, useRef } from 'react';
import { X, Plus, Lightbulb, BookOpen, Wrench, Star, Brain, ChefHat, Upload, Film, ChevronDown, ChevronRight } from 'lucide-react';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { supabaseService } from '../../lib/supabase-service';
import AvatarOrInitial from '../Family/AvatarOrInitial';


const categories = [
  { value: 'project', label: 'Project', icon: Wrench, description: 'Build or create something' },
  { value: 'class', label: 'Class', icon: Brain, description: 'Course or workshop' },
  { value: 'book', label: 'Book', icon: BookOpen, description: 'Reading recommendation' },
  { value: 'recipe', label: 'Recipe', icon: ChefHat, description: 'Cooking guide' },
  { value: 'movie', label: 'Movie/TV', icon: Film, description: 'Movie or TV show' },
];

const complexityLevels = [
  { value: 'beginner', label: 'Beginner', description: 'Quick & easy' },
  { value: 'intermediate', label: 'Intermediate', description: 'Some planning' },
  { value: 'portfolio', label: 'Portfolio', description: 'College-ready' },
];

const suggestedTags = [
  { text: 'College Portfolio', type: 'college' },
  { text: 'Life Skills', type: 'life-skills' },
  { text: 'Creative', type: 'creative' },
  { text: 'STEM', type: 'stem' },
  { text: 'Hands-on', type: '' },
  { text: 'Problem Solving', type: '' },
  { text: 'Critical Thinking', type: '' },
  { text: 'Research Skills', type: '' },
];

const bookGenres = [
  'Fiction', 'Non-fiction', 'Mystery', 'Thriller', 'Science Fiction', 'Fantasy', 
  'Romance', 'Historical Fiction', 'Biography', 'Memoir', 'Self-Help', 'Health & Wellness',
  'Business', 'Science', 'History', 'Philosophy', 'Psychology', 'Young Adult', 'Middle Grade'
];

const recipeTypes = [
  'Appetizer', 'Salad', 'Soup', 'Main Course', 'Side Dish', 'Dessert', 'Beverage', 
  'Snack', 'Breakfast', 'Lunch', 'Dinner', 'Brunch', 'Holiday Special', 'Quick & Easy'
];

export default function SmartIdeaCreationModal({ onClose, editingIdea = null }) {
  const { addIdea, editIdea, family, currentUser } = useFamilyContext();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef(null);
  
  // Loading states
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageError, setImageError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  
  // UI states
  const [showGettingStarted, setShowGettingStarted] = useState(false);

  // Form data - Initialize with safe defaults
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'project',
    complexity: null,
    
    // Common data - ensure arrays are always initialized
    targetMembers: [],
    tags: [],
    image: '',
    
    // Book-specific fields
    genre: '',
    isAudiobook: false,
    author: '',
    isbn: '',

  // Project Vision fields
  coreProblem: editingIdea?.coreProblem || editingIdea?.core_problem || '',
  realWorldContext: editingIdea?.realWorldContext || editingIdea?.real_world_context || '',
  skillsApplied: editingIdea?.skillsApplied || editingIdea?.skills_applied || '',
  
  // Pathway fields - Updated structure with focus/description separation
  pathways: editingIdea?.pathways || {
    research: { enabled: false, focus: '', description: '' },
    technical: { enabled: false, focus: '', description: '' },
    design: { enabled: false, focus: '', description: '' },
    business: { enabled: false, focus: '', description: '' }
  },
  recommendedTrack: editingIdea?.recommendedTrack || editingIdea?.recommended_track || '',
  
  // Portfolio & Success fields - Updated to open-ended
  portfolioDeliverables: editingIdea?.portfolioDeliverables || editingIdea?.portfolio_deliverables || '',
  successIndicators: editingIdea?.successIndicators || editingIdea?.success_indicators || '',
  
  // Resources & Extensions
  resourceClusters: Array.isArray(editingIdea?.resourceClusters)
    ? editingIdea.resourceClusters
    : Array.isArray(editingIdea?.resource_clusters)
    ? editingIdea.resource_clusters
    : [],
  extensionChallenges: editingIdea?.extensionChallenges || editingIdea?.extension_challenges || '',
  
  // Getting Started Steps
  gettingStartedSteps: editingIdea?.gettingStartedSteps || editingIdea?.getting_started_steps || '',
    
    // Recipe-specific fields
    recipeType: '',
    
    // Class-specific fields
    isVirtual: true,
    location: '',
    hostedBy: '',
    price: '',
    startDate: '',
    length: '',
    additionalDetails: '',
    classLink: '',
    
    // Movie/TV-specific fields
    rottenTomatoesCritic: '',
    rottenTomatoesAudience: '',
    googleAudienceScore: '',
    trailerUrl: '',
    releaseYear: '',
    director: '',
    isTV: false,
  });

  // Global paste listener for images
  useEffect(() => {
    const handleGlobalPaste = (e) => {
      // Only handle paste if we're in the image upload step and not already uploading
      if (currentStep === 2 && !uploadingImage && !formData.image) {
        const items = e.clipboardData?.items;
        if (items) {
          for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (item.type.startsWith('image/')) {
              const file = item.getAsFile();
              if (file) {
                handleImageUpload(file);
                e.preventDefault();
                break;
              }
            }
          }
        }
      }
    };

    document.addEventListener('paste', handleGlobalPaste);
    return () => document.removeEventListener('paste', handleGlobalPaste);
  }, [currentStep, uploadingImage, formData.image]);

  // Initialize form data for editing
  useEffect(() => {
    if (editingIdea) {
      // Parse target members from database
      const parseTargetMembers = () => {
        let targetMembers = editingIdea?.target_kids || editingIdea?.target_members || editingIdea?.targetKids || [];
        
        if (typeof targetMembers === 'string') {
          try {
            const parsed = JSON.parse(targetMembers);
            targetMembers = typeof parsed === 'string' ? JSON.parse(parsed) : parsed;
          } catch {
            targetMembers = [];
          }
        }
        
        return Array.isArray(targetMembers) ? targetMembers : [];
      };
      
      setFormData({
        title: editingIdea?.title || '',
        description: editingIdea?.description || '',
        category: editingIdea?.category || 'project',
        complexity: editingIdea?.complexity || null,
        
        // Common data
        targetMembers: parseTargetMembers(),
        tags: Array.isArray(editingIdea?.tags) ? editingIdea.tags : [],
        image: editingIdea?.image || '',
        
        // Book-specific fields
        genre: editingIdea?.genre || '',
        isAudiobook: editingIdea?.isAudiobook || editingIdea?.is_audiobook || false,
        author: editingIdea?.author || '',
        isbn: editingIdea?.isbn || '',
        
        // Recipe-specific fields
        recipeType: editingIdea?.recipeType || editingIdea?.recipe_type || '',
        
        // Class-specific fields
        isVirtual: editingIdea?.isVirtual || editingIdea?.is_virtual || true,
        location: editingIdea?.location || '',
        hostedBy: editingIdea?.hostedBy || editingIdea?.hosted_by || '',
        price: editingIdea?.price || '',
        startDate: editingIdea?.startDate || editingIdea?.start_date || '',
        length: editingIdea?.length || '',
        additionalDetails: editingIdea?.additionalDetails || editingIdea?.additional_details || '',
        classLink: editingIdea?.classLink || editingIdea?.class_link || '',
        
        // Project Vision fields
        coreProblem: editingIdea?.coreProblem || editingIdea?.core_problem || '',
        realWorldContext: editingIdea?.realWorldContext || editingIdea?.real_world_context || '',
        skillsApplied: editingIdea?.skillsApplied || editingIdea?.skills_applied || '',

                 // Pathway fields - Updated structure
         pathways: editingIdea?.pathways || {
           research: { enabled: false, focus: '', description: '' },
           technical: { enabled: false, focus: '', description: '' },
           design: { enabled: false, focus: '', description: '' },
           business: { enabled: false, focus: '', description: '' }
         },
         recommendedTrack: editingIdea?.recommendedTrack || editingIdea?.recommended_track || '',

         // Portfolio & Success fields - Updated to open-ended
         portfolioDeliverables: editingIdea?.portfolioDeliverables || editingIdea?.portfolio_deliverables || '',
         successIndicators: editingIdea?.successIndicators || editingIdea?.success_indicators || '',

        // Resources & Extensions
        resourceClusters: Array.isArray(editingIdea?.resourceClusters)
          ? editingIdea.resourceClusters
          : Array.isArray(editingIdea?.resource_clusters)
          ? editingIdea.resource_clusters
          : [],
                 extensionChallenges: editingIdea?.extensionChallenges || editingIdea?.extension_challenges || '',
         
         // Getting Started Steps
         gettingStartedSteps: editingIdea?.gettingStartedSteps || editingIdea?.getting_started_steps || '',

         // Movie/TV-specific fields
        rottenTomatoesCritic: editingIdea?.rottenTomatoesCritic || editingIdea?.rotten_tomatoes_critic || '',
        rottenTomatoesAudience: editingIdea?.rottenTomatoesAudience || editingIdea?.rotten_tomatoes_audience || '',
        googleAudienceScore: editingIdea?.googleAudienceScore || editingIdea?.google_audience_score || '',
        trailerUrl: editingIdea?.trailerUrl || editingIdea?.trailer_url || '',
        releaseYear: editingIdea?.releaseYear || editingIdea?.release_year || '',
        director: editingIdea?.director || '',
        isTV: editingIdea?.isTV || editingIdea?.is_tv || false,
      });
    }
  }, [editingIdea]);





  const addTag = (tagText, tagType = '') => {
    if (!formData.tags.find(tag => tag.text === tagText)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, { text: tagText, type: tagType }]
      }));
    }
  };

  const removeTag = (tagText) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag.text !== tagText)
    }));
  };

  const toggleMemberSelection = (memberId) => {
    console.log('🎯 toggleMemberSelection called with:', memberId);
    console.log('🎯 Current targetMembers:', formData.targetMembers);
    console.log('🎯 Includes check:', formData.targetMembers.includes(memberId));
    
    setFormData(prev => ({
      ...prev,
      targetMembers: prev.targetMembers.includes(memberId)
        ? prev.targetMembers.filter(id => id !== memberId)
        : [...prev.targetMembers, memberId]
    }));
  };

  // Parse getting started steps from text
  const parseGettingStartedSteps = (text) => {
    if (!text) return [];
    
    const steps = [];
    const lines = text.split('\n').map(line => line.trim()).filter(line => line);
    
    let currentStep = null;
    
    for (let line of lines) {
      // Check if line starts with a number (step title)
      const stepMatch = line.match(/^(\d+)\.\s*(.+)$/);
      
      if (stepMatch) {
        // Save previous step if exists
        if (currentStep) {
          steps.push(currentStep);
        }
        
        // Start new step
        currentStep = {
          number: parseInt(stepMatch[1]),
          title: stepMatch[2].trim(),
          description: ''
        };
      } else if (currentStep && line) {
        // Add to description of current step
        currentStep.description += (currentStep.description ? ' ' : '') + line;
      }
    }
    
    // Add the last step
    if (currentStep) {
      steps.push(currentStep);
    }
    
    return steps;
  };

  // Image upload handlers
  const handleImageUpload = async (file) => {
    if (!file || !file.type.startsWith('image/')) {
      setImageError('Please select a valid image file.');
      return;
    }
    
    setUploadingImage(true);
    setImageError(null);
    
    try {
      const result = await supabaseService.uploadImage(file, family.id);
      setFormData(prev => ({ ...prev, image: result.url }));
    } catch (error) {
      console.error('Image upload error:', error);
      setImageError(error.message || 'Failed to upload image. Please try again.');
    } finally {
      setUploadingImage(false);
    }
  };



  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImageUpload(file);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleImageUpload(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (items) {
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) {
            handleImageUpload(file);
            e.preventDefault();
            break;
          }
        }
      }
    }
  };

  const removeImage = () => {
    setFormData(prev => ({ ...prev, image: '' }));
    setImageError(null);
  };

  const nextStep = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      const ideaData = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        complexity: formData.complexity,
        targetKids: formData.targetMembers, // Use new targetMembers field
        tags: formData.tags,
        image: formData.image || null,
        // Category-specific fields
        genre: formData.category === 'book' ? formData.genre : undefined,
        isAudiobook: formData.category === 'book' ? formData.isAudiobook : undefined,
        author: formData.category === 'book' ? formData.author : undefined,
        isbn: formData.category === 'book' ? formData.isbn : undefined,
        recipeType: formData.category === 'recipe' ? formData.recipeType : undefined,
        // Class-specific fields
        isVirtual: formData.category === 'class' ? formData.isVirtual : undefined,
        location: formData.category === 'class' ? formData.location : undefined,
        hostedBy: formData.category === 'class' ? formData.hostedBy : undefined,
        price: formData.category === 'class' ? formData.price : undefined,  
        startDate: formData.category === 'class' ? formData.startDate : undefined,
        length: formData.category === 'class' ? formData.length : undefined,
        additionalDetails: formData.category === 'class' ? formData.additionalDetails : undefined,
        classLink: formData.category === 'class' ? formData.classLink : undefined,
                 // Project-specific fields (only when category is 'project')
         coreProblem: formData.category === 'project' ? formData.coreProblem : undefined,
         realWorldContext: formData.category === 'project' ? formData.realWorldContext : undefined,
         skillsApplied: formData.category === 'project' ? formData.skillsApplied : undefined,
         pathways: formData.category === 'project' ? formData.pathways : undefined,
         recommendedTrack: formData.category === 'project' ? formData.recommendedTrack : undefined,
         portfolioDeliverables: formData.category === 'project' ? formData.portfolioDeliverables : undefined,
         successIndicators: formData.category === 'project' ? formData.successIndicators : undefined,
         resourceClusters: formData.category === 'project' ? formData.resourceClusters : undefined,
         extensionChallenges: formData.category === 'project' ? formData.extensionChallenges : undefined,
         gettingStartedSteps: formData.category === 'project' ? formData.gettingStartedSteps : undefined,
        // Movie/TV-specific fields
        rottenTomatoesCritic: formData.category === 'movie' ? (formData.rottenTomatoesCritic ? parseInt(formData.rottenTomatoesCritic) : undefined) : undefined,
        rottenTomatoesAudience: formData.category === 'movie' ? (formData.rottenTomatoesAudience ? parseInt(formData.rottenTomatoesAudience) : undefined) : undefined,
        googleAudienceScore: formData.category === 'movie' ? (formData.googleAudienceScore ? parseInt(formData.googleAudienceScore) : undefined) : undefined,
        trailerUrl: formData.category === 'movie' ? formData.trailerUrl : undefined,
        releaseYear: formData.category === 'movie' ? (formData.releaseYear ? parseInt(formData.releaseYear) : undefined) : undefined,
        director: formData.category === 'movie' ? formData.director : undefined,
        isTV: formData.category === 'movie' ? formData.isTV : undefined,
      };
      
      if (editingIdea) {
        await editIdea(editingIdea.id, ideaData);
      } else {
        await addIdea(ideaData);
      }
      
      onClose();
    } catch (error) {
      console.error('❌ Error saving idea:', error);
      alert(`Error saving idea: ${error.message}. Please try again.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = () => {
    // Don't allow form submission while images are uploading
    if (uploadingImage) {
      return false;
    }
    
    // No requirement for targetMembers - empty means "everyone"
    return formData.title.trim() && formData.description.trim();
  };

  const isStepValid = (step) => {
    switch (step) {
      case 1:
        // No requirement for targetMembers - empty means "everyone"
        return formData.title.trim() && formData.description.trim();
      case 2:
        return true; // Step 2 is just additional details, always valid
      default:
        return true;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-6xl w-full max-h-[95vh] overflow-hidden flex">
        {/* Main Form Panel */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-8">
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-bold text-stone-900">
                {editingIdea ? 'Edit Idea' : 'Add New Idea'}
              </h2>
              <button
                onClick={onClose}
                className="p-2 text-stone-400 hover:text-stone-600 hover:bg-stone-100 rounded-lg transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Step Indicator */}
            <div className="flex gap-2 mb-8">
              {[1, 2].map(step => (
                <div
                  key={step}
                  className={`w-2 h-2 rounded-full transition-all ${
                    step <= currentStep 
                      ? step === currentStep ? 'bg-teal-500 scale-125' : 'bg-teal-600'
                      : 'bg-stone-300'
                  }`}
                />
              ))}
            </div>

            {/* Step 1: Basic Info */}
            {currentStep === 1 && (
              <div className="space-y-8">
                <div>
                  <h3 className="text-xl font-semibold mb-4">Add a new idea</h3>
                  <p className="text-stone-600 mb-6">Share something great with your family</p>
                </div>

                {/* Who is this for? */}
                <div>
                  <h4 className="text-lg font-semibold mb-4">
                    Send to: {formData.targetMembers.length === 0 ? 'Everyone' : `${formData.targetMembers.length} selected`}
                  </h4>
                  
                  <div className="flex flex-wrap gap-3">
                    {/* Me (current user) */}
                    {currentUser && (
                      <button
                        onClick={() => toggleMemberSelection(currentUser.id)}
                        className={`relative w-16 h-16 rounded-full border-4 transition-all ${
                          formData.targetMembers.includes(currentUser.id)
                            ? 'border-teal-500 ring-2 ring-teal-200'
                            : 'border-stone-300 hover:border-stone-400'
                        }`}
                      >
                        <AvatarOrInitial
                          avatar={currentUser.avatar}
                          name={currentUser.name || 'Me'}
                          size="2xl"
                        />
                      </button>
                    )}
                    
                    {/* All family members (excluding current user to avoid duplicates) */}
                    {family?.parents && family.parents
                      .filter(parent => parent.id !== currentUser?.id)
                      .map(parent => (
                        <button
                          key={parent.id}
                          onClick={() => toggleMemberSelection(parent.id)}
                          className={`relative w-16 h-16 rounded-full border-4 transition-all ${
                            formData.targetMembers.includes(parent.id)
                              ? 'border-teal-500 ring-2 ring-teal-200'
                              : 'border-stone-300 hover:border-stone-400'
                          }`}
                        >
                          <AvatarOrInitial
                            avatar={parent.avatar}
                            name={parent.name}
                            size="2xl"
                          />
                        </button>
                      ))}
                    
                    {family?.kids && family.kids
                      .filter(kid => kid.id !== currentUser?.id)
                      .map(kid => (
                        <button
                          key={kid.id}
                          onClick={() => toggleMemberSelection(kid.id)}
                          className={`relative w-16 h-16 rounded-full border-4 transition-all ${
                            formData.targetMembers.includes(kid.id)
                              ? 'border-teal-500 ring-2 ring-teal-200'
                              : 'border-stone-300 hover:border-stone-400'
                          }`}
                        >
                          <AvatarOrInitial
                            avatar={kid.avatar}
                            name={kid.name}
                            size="2xl"
                          />
                        </button>
                      ))}
                  </div>
                  
                  <p className="text-sm text-stone-500 mt-3">
                    {formData.targetMembers.length === 0 
                      ? 'No one selected - will send to everyone' 
                      : 'Tap avatars to select recipients'
                    }
                  </p>
                </div>

                {/* Type Selection - More Concise */}
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-3">Type</label>
                  <div className="flex flex-wrap gap-2">
                    {categories.map(category => {
                      const IconComponent = category.icon;
                      return (
                        <button
                          key={category.value}
                          onClick={() => setFormData(prev => ({ ...prev, category: category.value }))}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all ${
                            formData.category === category.value
                              ? 'border-teal-500 bg-teal-50 text-teal-700'
                              : 'border-stone-200 hover:border-stone-300 text-stone-700'
                          }`}
                        >
                          <IconComponent className="w-4 h-4" />
                          <span className="text-sm font-medium">{category.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Title
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-4 py-3 text-lg border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    placeholder="Enter your idea..."
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                    className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    placeholder="What is this idea about and why is it interesting?"
                  />
                </div>
                
                {/* Feature Image Upload */}
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-4">Image (optional)</label>
                  <p className="text-stone-600 mb-4">Add a visual to make your idea more engaging</p>
                  

                  {formData.image ? (
                    <div className="relative group bg-stone-50 rounded-xl overflow-hidden">
                      <img 
                        src={formData.image} 
                        alt="Feature image preview" 
                        className="w-full h-48 object-cover"
                      />
                      <button
                        onClick={removeImage}
                        className="absolute top-3 right-3 p-2 bg-red-500 hover:bg-red-600 text-white rounded-full opacity-90 hover:opacity-100 transition-all"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div
                      onDrop={handleDrop}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onPaste={handlePaste}
                      onClick={() => !uploadingImage && fileInputRef.current?.click()}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          if (!uploadingImage) fileInputRef.current?.click();
                        }
                      }}
                      tabIndex={0}
                      role="button"
                      aria-label="Upload image by clicking, dragging, or pasting"
                      className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                        uploadingImage ? 'opacity-75 cursor-not-allowed' :
                        dragOver ? 'border-teal-500 bg-teal-50' : 'border-stone-300 hover:border-stone-400'
                      }`}
                    >
                      {uploadingImage ? (
                        <div className="flex flex-col items-center gap-3">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
                          <p className="text-sm text-stone-600">Uploading image...</p>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-3">
                          <Upload className="w-8 h-8 text-stone-400" />
                          <div>
                            <p className="text-sm font-medium text-stone-700">Drag & drop an image, or click to browse</p>
                            <p className="text-xs text-stone-500 mt-1">You can also paste an image (Ctrl/Cmd + V)</p>
                          </div>
                        </div>
                      )}
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleFileSelect}
                        className="hidden"
                      />
                    </div>
                  )}
                  
                  {imageError && (
                    <p className="text-red-600 text-sm mt-2 flex items-center gap-2">
                      <X className="w-4 h-4" />
                      {imageError}
                    </p>
                  )}
                </div>

                {/* Tags */}
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-4">Tags (optional)</label>
                  <p className="text-stone-600 mb-4">Help categorize and highlight key benefits</p>
                  
                  {/* Current tags */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {formData.tags.map((tag, index) => (
                      <span
                        key={index}
                        className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2 ${
                          tag.type === 'college' 
                            ? 'bg-violet-100 text-violet-600' 
                            : 'bg-stone-100 text-stone-600'
                        }`}
                      >
                        {tag.text}
                        <button
                          onClick={() => removeTag(tag.text)}
                          className="text-current hover:text-red-600"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                  
                  {/* Suggested tags */}
                  <div className="flex flex-wrap gap-2">
                    {suggestedTags.map((tag, index) => (
                      <button
                        key={index}
                        onClick={() => addTag(tag.text, tag.type)}
                        className="px-3 py-1 rounded-full text-sm font-medium bg-stone-50 text-stone-600 border border-stone-200 hover:bg-stone-100 transition-colors"
                      >
                        {tag.text}
                      </button>
                    ))}
                  </div>
                </div>
                



              </div>
            )}


            {/* Step 2: Type-Specific Details */}
            {currentStep === 2 && (
              <div className="space-y-8">
                <div>
                  <h3 className="text-xl font-semibold mb-4">Additional Details</h3>
                  <p className="text-stone-600 mb-6">Add specific information for this type of {formData.category}</p>
                </div>

                {/* Project-specific fields */}
                // Replace the existing project-specific fields section in Step 2

{formData.category === 'project' && (
  <div className="space-y-8">
    {/* Complexity Level */}
    <div>
      <div className="flex items-center justify-between mb-6">
        <h4 className="text-lg font-semibold">Project complexity</h4>
        <span className="text-sm text-stone-500 italic">Optional</span>
      </div>
      <div className="grid grid-cols-4 gap-4">
        <button
          onClick={() => setFormData(prev => ({ ...prev, complexity: null }))}
          className={`p-4 rounded-xl border-2 transition-all text-center ${
            !formData.complexity
              ? 'border-teal-500 bg-teal-50'
              : 'border-stone-200 hover:border-stone-300'
          }`}
        >
          <div className="font-medium text-sm">Not Specified</div>
          <div className="text-xs text-stone-500 mt-1">Skip complexity</div>
        </button>
        {complexityLevels.map(level => (
          <button
            key={level.value}
            onClick={() => setFormData(prev => ({ ...prev, complexity: level.value }))}
            className={`p-4 rounded-xl border-2 transition-all text-center ${
              formData.complexity === level.value
                ? 'border-teal-500 bg-teal-50'
                : 'border-stone-200 hover:border-stone-300'
            }`}
          >
            <div className="font-medium text-sm">{level.label}</div>
            <div className="text-xs text-stone-500">{level.description}</div>
          </button>
        ))}
      </div>
    </div>

    {/* Project Vision */}
    <div>
      <h4 className="text-lg font-semibold mb-4">Project Vision</h4>
      <p className="text-stone-600 mb-6">Define the real-world context and opportunity</p>
      
      <div className="space-y-6">
        {/* Core Problem/Opportunity */}
        <div>
          <label className="block text-sm font-medium text-stone-700 mb-2">
            Core Problem or Opportunity
          </label>
          <textarea
            value={formData.coreProblem || ''}
            onChange={(e) => setFormData(prev => ({ ...prev, coreProblem: e.target.value }))}
            rows={3}
            className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            placeholder="What real-world gap or challenge does this address? What opportunity exists?"
          />
        </div>

        {/* Real-World Context */}
        <div>
          <label className="block text-sm font-medium text-stone-700 mb-2">
            Why This Matters
          </label>
          <textarea
            value={formData.realWorldContext || ''}
            onChange={(e) => setFormData(prev => ({ ...prev, realWorldContext: e.target.value }))}
            rows={2}
            className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            placeholder="Who benefits from solving this? What's the potential impact?"
          />
        </div>

        {/* Cross-Disciplinary Connections */}
        <div>
          <label className="block text-sm font-medium text-stone-700 mb-2">
            Skills & Disciplines Applied
          </label>
          <input
            type="text"
            value={formData.skillsApplied || ''}
            onChange={(e) => setFormData(prev => ({ ...prev, skillsApplied: e.target.value }))}
            className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            placeholder="Engineering, design thinking, data analysis, business strategy, etc."
          />
        </div>
      </div>
    </div>

         {/* Exploration Pathways */}
     <div>
       <h4 className="text-lg font-semibold mb-4">Exploration Pathways</h4>
       <p className="text-stone-600 mb-6">Select relevant tracks and define focus areas</p>
       
       <div className="space-y-6">
         {/* Recommended Starting Track */}
         <div className="bg-azure-50 border border-azure-200 rounded-lg p-4">
           <h5 className="font-medium text-azure-900 mb-3">Recommended Starting Track</h5>
           <div className="flex flex-wrap gap-3">
             {Object.entries({
               research: '🔍 Research',
               technical: '⚙️ Technical', 
               design: '🎨 Design',
               business: '💼 Business'
             }).map(([track, label]) => (
               <button
                 key={track}
                 onClick={() => setFormData(prev => ({ ...prev, recommendedTrack: track }))}
                 className={`px-3 py-2 rounded-lg border-2 text-sm transition-all ${
                   formData.recommendedTrack === track
                     ? 'border-azure-500 bg-azure-100 text-azure-700'
                     : 'border-azure-200 bg-white text-azure-600 hover:border-azure-300'
                 }`}
               >
                 {label}
               </button>
             ))}
           </div>
         </div>

         {/* Individual Track Configuration */}
         {Object.entries({
           research: { emoji: '🔍', label: 'Research Track', defaultFocus: 'Market analysis, user research, competitive landscape' },
           technical: { emoji: '⚙️', label: 'Technical Track', defaultFocus: 'Prototyping, modeling, testing, engineering solutions' },
           design: { emoji: '🎨', label: 'Design Track', defaultFocus: 'User experience, accessibility, aesthetics, human-centered design' },
           business: { emoji: '💼', label: 'Business Track', defaultFocus: 'Viability analysis, market strategy, financial modeling' }
         }).map(([trackKey, track]) => (
           <div key={trackKey} className={`border rounded-lg p-4 transition-all ${
             formData.pathways?.[trackKey]?.enabled 
               ? 'border-teal-200 bg-teal-50' 
               : 'border-stone-200 bg-stone-50'
           }`}>
             {/* Track Header with Enable/Disable */}
             <div className="flex items-center gap-3 mb-4">
               <input
                 type="checkbox"
                 id={`${trackKey}-enabled`}
                 checked={formData.pathways?.[trackKey]?.enabled || false}
                 onChange={(e) => setFormData(prev => ({
                   ...prev,
                   pathways: { 
                     ...prev.pathways, 
                     [trackKey]: { 
                       ...prev.pathways?.[trackKey], 
                       enabled: e.target.checked 
                     } 
                   }
                 }))}
                 className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-stone-300 rounded"
               />
               <label htmlFor={`${trackKey}-enabled`} className="font-medium text-stone-700 flex items-center gap-2">
                 <span>{track.emoji}</span>
                 <span>{track.label}</span>
                 {formData.recommendedTrack === trackKey && (
                   <span className="text-xs bg-azure-100 text-azure-700 px-2 py-1 rounded-full">Recommended</span>
                 )}
               </label>
             </div>

             {/* Track Content (only when enabled) */}
             {formData.pathways?.[trackKey]?.enabled && (
               <div className="space-y-3 ml-7">
                 {/* Focus Field */}
                 <div>
                   <label className="block text-sm font-medium text-stone-700 mb-1">Focus Areas</label>
                   <input
                     type="text"
                     value={formData.pathways?.[trackKey]?.focus || ''}
                     onChange={(e) => setFormData(prev => ({
                       ...prev,
                       pathways: { 
                         ...prev.pathways, 
                         [trackKey]: { 
                           ...prev.pathways?.[trackKey], 
                           focus: e.target.value 
                         } 
                       }
                     }))}
                     placeholder={track.defaultFocus}
                     className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                   />
                 </div>

                 {/* Description Field */}
                 <div>
                   <label className="block text-sm font-medium text-stone-700 mb-1">Activities & Methods</label>
                   <textarea
                     value={formData.pathways?.[trackKey]?.description || ''}
                     onChange={(e) => setFormData(prev => ({
                       ...prev,
                       pathways: { 
                         ...prev.pathways, 
                         [trackKey]: { 
                           ...prev.pathways?.[trackKey], 
                           description: e.target.value 
                         } 
                       }
                     }))}
                     rows={2}
                     className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                     placeholder="Describe specific activities, methods, and deliverables for this track..."
                   />
                 </div>
               </div>
             )}
           </div>
         ))}
       </div>
     </div>

         {/* Portfolio Deliverables - Open-Ended */}
     <div>
       <h4 className="text-lg font-semibold mb-4">Portfolio Deliverables</h4>
       <p className="text-stone-600 mb-4">What could students create to showcase their work?</p>
       
       <textarea
         value={formData.portfolioDeliverables || ''}
         onChange={(e) => setFormData(prev => ({ ...prev, portfolioDeliverables: e.target.value }))}
         rows={6}
         className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
         placeholder="• Process documentation (research, iterations, decisions)
• Final prototype or analysis
• Presentation or pitch deck
• Written reflection on learning
• Distribution/sharing plan
• Video demonstration
• Case study write-up

Feel free to customize these suggestions or add your own ideas..."
       />
     </div>

    {/* Success Indicators */}
    <div>
      <h4 className="text-lg font-semibold mb-4">Success Indicators</h4>
      <p className="text-stone-600 mb-4">What does good work look like for this project?</p>
      
      <textarea
        value={formData.successIndicators || ''}
        onChange={(e) => setFormData(prev => ({ ...prev, successIndicators: e.target.value }))}
        rows={4}
        className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
        placeholder="• Demonstrates clear understanding of the problem
• Shows evidence of research and iteration
• Presents findings in a professional manner
• Makes meaningful connections between concepts
• Proposes actionable next steps..."
      />
    </div>

    {/* Resource Clusters */}
    <div>
      <h4 className="text-lg font-semibold mb-4">Starting Resources</h4>
      <p className="text-stone-600 mb-4">Key resources to help students get started</p>
      
      <div className="space-y-3">
        {(formData.resourceClusters || []).map((resource, index) => (
          <div key={index} className="flex gap-2">
            <input
              type="text"
              value={resource.title || ''}
              onChange={(e) => {
                const updated = [...(formData.resourceClusters || [])];
                updated[index] = { ...updated[index], title: e.target.value };
                setFormData(prev => ({ ...prev, resourceClusters: updated }));
              }}
              placeholder="Resource title"
              className="flex-1 px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            />
            <input
              type="url"
              value={resource.url || ''}
              onChange={(e) => {
                const updated = [...(formData.resourceClusters || [])];
                updated[index] = { ...updated[index], url: e.target.value };
                setFormData(prev => ({ ...prev, resourceClusters: updated }));
              }}
              placeholder="https://..."
              className="flex-1 px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            />
            <button
              onClick={() => {
                const updated = (formData.resourceClusters || []).filter((_, i) => i !== index);
                setFormData(prev => ({ ...prev, resourceClusters: updated }));
              }}
              className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              ×
            </button>
          </div>
        ))}
        
        <button
          onClick={() => {
            const current = formData.resourceClusters || [];
            setFormData(prev => ({
              ...prev,
              resourceClusters: [...current, { title: '', url: '' }]
            }));
          }}
          className="text-sm text-teal-600 hover:text-teal-700 font-medium flex items-center gap-1"
        >
          + Add Resource
        </button>
      </div>
    </div>

         {/* Extension Challenges */}
     <div>
       <h4 className="text-lg font-semibold mb-4">Extension Challenges</h4>
       <p className="text-stone-600 mb-4">Optional challenges for deeper exploration</p>
       
       <textarea
         value={formData.extensionChallenges || ''}
         onChange={(e) => setFormData(prev => ({ ...prev, extensionChallenges: e.target.value }))}
         rows={3}
         className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
         placeholder="• Partner with a local business to test your solution
• Present findings to subject matter experts
• Create a scaled version for a different context
• Develop a business plan around your solution..."
       />
     </div>

     {/* Getting Started Steps */}
     <div>
       <button
         type="button"
         onClick={() => setShowGettingStarted(!showGettingStarted)}
         className="flex items-center gap-2 w-full text-left mb-4 hover:bg-stone-50 p-2 rounded-lg transition-colors"
       >
         {showGettingStarted ? (
           <ChevronDown className="w-5 h-5 text-stone-500" />
         ) : (
           <ChevronRight className="w-5 h-5 text-stone-500" />
         )}
         <h4 className="text-lg font-semibold">Getting Started Steps</h4>
         <span className="text-sm text-stone-500">(Optional)</span>
       </button>
       
       {showGettingStarted && (
         <div className="space-y-4">
           <p className="text-stone-600">
             Provide a step-by-step guide to help students begin the project
           </p>
           
           {/* Input Area */}
           <div>
             <textarea
               value={formData.gettingStartedSteps || ''}
               onChange={(e) => setFormData(prev => ({ ...prev, gettingStartedSteps: e.target.value }))}
               rows={8}
               className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
               placeholder="1. Research the Problem
Spend time understanding the challenge and gathering background information.

2. Define Your Approach  
Choose which pathway(s) you'll focus on and create a plan.

3. Create Initial Prototypes
Start with simple mockups or basic versions to test ideas.

4. Gather Feedback
Share your early work and collect input from others."
             />
           </div>
           
           {/* Preview of Parsed Steps */}
           {formData.gettingStartedSteps && (
             <div className="bg-stone-50 border border-stone-200 rounded-lg p-4">
               <h5 className="text-sm font-medium text-stone-700 mb-3">Preview:</h5>
               <div className="space-y-3">
                 {parseGettingStartedSteps(formData.gettingStartedSteps).map((step, index) => (
                   <div key={index} className="flex gap-3">
                     <div className="w-6 h-6 bg-teal-100 text-teal-700 rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">
                       {step.number}
                     </div>
                     <div className="flex-1">
                       <div className="font-medium text-stone-900">{step.title}</div>
                       {step.description && (
                         <div className="text-sm text-stone-600 mt-1">{step.description}</div>
                       )}
                     </div>
                   </div>
                 ))}
               </div>
             </div>
           )}
         </div>
       )}
     </div>
   </div>
)}

                {/* Class-specific fields */}
                {formData.category === 'class' && (
                  <div>
                    <h4 className="text-lg font-semibold mb-6">Class Details</h4>
                    <div className="space-y-6">
                      {/* Virtual/In-Person */}
                      <div>
                        <label className="block text-sm font-medium text-stone-700 mb-3">Format</label>
                        <div className="flex gap-4">
                          <button
                            onClick={() => setFormData(prev => ({ ...prev, isVirtual: true }))}
                            className={`px-4 py-2 rounded-lg border-2 transition-all ${
                              formData.isVirtual
                                ? 'border-teal-500 bg-teal-50 text-teal-700'
                                : 'border-stone-200 hover:border-stone-300'
                            }`}
                          >
                            Virtual
                          </button>
                          <button
                            onClick={() => setFormData(prev => ({ ...prev, isVirtual: false }))}
                            className={`px-4 py-2 rounded-lg border-2 transition-all ${
                              !formData.isVirtual
                                ? 'border-teal-500 bg-teal-50 text-teal-700'
                                : 'border-stone-200 hover:border-stone-300'
                            }`}
                          >
                            In-Person
                          </button>
                        </div>
                      </div>

                      {/* Location (if in-person) */}
                      {!formData.isVirtual && (
                        <div>
                          <label className="block text-sm font-medium text-stone-700 mb-2">Location</label>
                          <input
                            type="text"
                            value={formData.location}
                            onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                            className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                            placeholder="Enter location address or venue"
                          />
                        </div>
                      )}

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Hosted by */}
                        <div>
                          <label className="block text-sm font-medium text-stone-700 mb-2">Hosted by</label>
                          <input
                            type="text"
                            value={formData.hostedBy}
                            onChange={(e) => setFormData(prev => ({ ...prev, hostedBy: e.target.value }))}
                            className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                            placeholder="Organization or instructor name"
                          />
                        </div>

                        {/* Price */}
                        <div>
                          <label className="block text-sm font-medium text-stone-700 mb-2">Price</label>
                          <input
                            type="text"
                            value={formData.price}
                            onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                            className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                            placeholder="Free, $50, $100/month, etc."
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Start Date */}
                        <div>
                          <label className="block text-sm font-medium text-stone-700 mb-2">Start Date</label>
                          <input
                            type="date"
                            value={formData.startDate}
                            onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
                            className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                          />
                        </div>

                        {/* Length */}
                        <div>
                          <label className="block text-sm font-medium text-stone-700 mb-2">Length</label>
                          <input
                            type="text"
                            value={formData.length}
                            onChange={(e) => setFormData(prev => ({ ...prev, length: e.target.value }))}
                            className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                            placeholder="6 weeks, 3 months, self-paced, etc."
                          />
                        </div>
                      </div>

                      {/* Additional Details */}
                      <div>
                        <label className="block text-sm font-medium text-stone-700 mb-2">Additional Details</label>
                        <textarea
                          value={formData.additionalDetails}
                          onChange={(e) => setFormData(prev => ({ ...prev, additionalDetails: e.target.value }))}
                          rows={3}
                          className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                          placeholder="Prerequisites, materials needed, special notes..."
                        />
                      </div>

                      {/* Link */}
                      <div>
                        <label className="block text-sm font-medium text-stone-700 mb-2">Link</label>
                        <input
                          type="url"
                          value={formData.classLink}
                          onChange={(e) => setFormData(prev => ({ ...prev, classLink: e.target.value }))}
                          className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                          placeholder="https://example.com/class"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Book-specific fields */}
                {formData.category === 'book' && (
                  <div>
                    <h3 className="text-xl font-semibold mb-6">Book Details</h3>
                    <div className="space-y-6">
                      {/* Genre */}
                      <div>
                        <label className="block text-sm font-medium text-stone-700 mb-2">Genre</label>
                        <select
                          value={formData.genre}
                          onChange={(e) => setFormData(prev => ({ ...prev, genre: e.target.value }))}
                          className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                        >
                          <option value="">Select a genre (optional)</option>
                          {bookGenres.map(genre => (
                            <option key={genre} value={genre}>{genre}</option>
                          ))}
                        </select>
                      </div>

                      {/* Author */}
                      <div>
                        <label className="block text-sm font-medium text-stone-700 mb-2">Author</label>
                        <input
                          type="text"
                          value={formData.author}
                          onChange={(e) => setFormData(prev => ({ ...prev, author: e.target.value }))}
                          className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                          placeholder="Author name (optional)"
                        />
                      </div>

                      {/* Audiobook checkbox */}
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="audiobook"
                          checked={formData.isAudiobook}
                          onChange={(e) => setFormData(prev => ({ ...prev, isAudiobook: e.target.checked }))}
                          className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-stone-300 rounded"
                        />
                        <label htmlFor="audiobook" className="ml-2 block text-sm text-stone-700">
                          This is an audiobook
                        </label>
                      </div>

                      {/* ISBN */}
                      <div>
                        <label className="block text-sm font-medium text-stone-700 mb-2">ISBN (optional)</label>
                        <input
                          type="text"
                          value={formData.isbn}
                          onChange={(e) => setFormData(prev => ({ ...prev, isbn: e.target.value }))}
                          className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                          placeholder="ISBN number"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Recipe-specific fields */}
                {formData.category === 'recipe' && (
                  <div>
                    <h3 className="text-xl font-semibold mb-6">Recipe Details</h3>
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-2">Recipe Type</label>
                      <select
                        value={formData.recipeType}
                        onChange={(e) => setFormData(prev => ({ ...prev, recipeType: e.target.value }))}
                        className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      >
                        <option value="">Select a recipe type (optional)</option>
                        {recipeTypes.map(recipeType => (
                          <option key={recipeType} value={recipeType}>{recipeType}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {/* Movie/TV-specific fields */}
                {formData.category === 'movie' && (
                  <div>
                    <h3 className="text-xl font-semibold mb-6">Movie/TV Details</h3>
                    <div className="space-y-6">
                      {/* Movie vs TV show */}
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="isTV"
                          checked={formData.isTV}
                          onChange={(e) => setFormData(prev => ({ ...prev, isTV: e.target.checked }))}
                          className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-stone-300 rounded"
                        />
                        <label htmlFor="isTV" className="ml-2 block text-sm text-stone-700">
                          This is a TV show (uncheck for movies)
                        </label>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Director */}
                        <div>
                          <label className="block text-sm font-medium text-stone-700 mb-2">Director</label>
                          <input
                            type="text"
                            value={formData.director}
                            onChange={(e) => setFormData(prev => ({ ...prev, director: e.target.value }))}
                            className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                            placeholder="Director name (optional)"
                          />
                        </div>

                        {/* Release Year */}
                        <div>
                          <label className="block text-sm font-medium text-stone-700 mb-2">Release Year</label>
                          <input
                            type="number"
                            value={formData.releaseYear}
                            onChange={(e) => setFormData(prev => ({ ...prev, releaseYear: e.target.value }))}
                            className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                            placeholder="2024"
                            min="1900"
                            max="2030"
                          />
                        </div>
                      </div>

                      {/* Rotten Tomatoes Scores */}
                      <div>
                        <h4 className="text-sm font-medium text-stone-700 mb-4">Rotten Tomatoes Scores (optional)</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs text-stone-600 mb-1">Critic Score (%)</label>
                            <input
                              type="number"
                              value={formData.rottenTomatoesCritic}
                              onChange={(e) => setFormData(prev => ({ ...prev, rottenTomatoesCritic: e.target.value }))}
                              className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                              placeholder="85"
                              min="0"
                              max="100"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-stone-600 mb-1">Audience Score (%)</label>
                            <input
                              type="number"
                              value={formData.rottenTomatoesAudience}
                              onChange={(e) => setFormData(prev => ({ ...prev, rottenTomatoesAudience: e.target.value }))}
                              className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                              placeholder="92"
                              min="0"
                              max="100"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Google Audience Score */}
                      <div>
                        <label className="block text-sm font-medium text-stone-700 mb-2">Google Audience Score (%)</label>
                        <input
                          type="number"
                          value={formData.googleAudienceScore}
                          onChange={(e) => setFormData(prev => ({ ...prev, googleAudienceScore: e.target.value }))}
                          className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                          placeholder="89"
                          min="0"
                          max="100"
                        />
                      </div>

                      {/* Trailer URL */}
                      <div>
                        <label className="block text-sm font-medium text-stone-700 mb-2">Trailer URL</label>
                        <input
                          type="url"
                          value={formData.trailerUrl}
                          onChange={(e) => setFormData(prev => ({ ...prev, trailerUrl: e.target.value }))}
                          className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                          placeholder="https://youtube.com/watch?v=..."
                        />
                      </div>
                    </div>
                  </div>
                )}

              </div>
            )}
            {/* Navigation */}
            <div className="flex justify-between items-center pt-8 border-t border-stone-200 relative z-10">
              <button
                onClick={prevStep}
                className={`px-6 py-3 border border-stone-300 text-stone-600 font-medium rounded-xl hover:bg-stone-50 transition-colors ${
                  currentStep === 1 ? 'invisible' : ''
                }`}
              >
                ← Back
              </button>
              
              <div className="flex-1"></div>
              
              {currentStep < 2 ? (
                <button
                  onClick={nextStep}
                  disabled={!isStepValid(currentStep)}
                  className="px-6 py-3 bg-teal-600 text-white font-medium rounded-xl hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors relative z-10"
                >
                  Next →
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={!isFormValid() || isSubmitting}
                  className="px-8 py-3 bg-teal-600 text-white font-medium rounded-xl hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      {editingIdea ? 'Saving...' : 'Publishing...'}
                    </span>
                  ) : (
                    <>✨ {editingIdea ? 'Save Changes' : 'Publish Idea'}</>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Preview Panel */}
        <div className="w-80 bg-stone-50 border-l border-stone-200 p-6 overflow-y-auto">
          <h3 className="text-lg font-semibold text-stone-900 mb-4">Preview</h3>
          
          {/* Preview Card */}
          <div className="bg-white rounded-xl overflow-hidden border border-stone-200 mb-4">
            {/* Hero Section */}
            <div className="h-32 bg-gradient-to-br from-teal-500 to-teal-600 relative overflow-hidden">
              {/* Feature Image Background */}
              {formData.image && (
                <div className="absolute inset-0">
                  <img 
                    src={formData.image} 
                    alt="Feature image" 
                    className="w-full h-full object-cover opacity-90"
                  />
                  <div className="absolute inset-0 bg-teal-500 bg-opacity-30"></div>
                </div>
              )}
              
              {/* Image Upload Indicator */}
              {uploadingImage && (
                <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-20">
                  <div className="flex flex-col items-center gap-2 text-white">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                    <span className="text-xs font-medium">Uploading image...</span>
                  </div>
                </div>
              )}
              <div className="absolute -top-4 -right-4 w-16 h-16 bg-white bg-opacity-10 rounded-full"></div>
              <div className="absolute inset-0 flex items-center justify-center z-10">
                <span className="text-3xl text-white opacity-90 drop-shadow-md">
                  {categories.find(c => c.value === formData.category)?.icon ? 
                    React.createElement(categories.find(c => c.value === formData.category).icon, { className: "w-8 h-8" }) : 
                    '💡'
                  }
                </span>
              </div>
              {formData.complexity && formData.category === 'project' && (
                <div className="absolute top-2 right-2 bg-white bg-opacity-95 px-2 py-1 rounded-full">
                  <span className="text-xs font-semibold text-slate-900">
                    {complexityLevels.find(l => l.value === formData.complexity)?.label}
                  </span>
                </div>
              )}
            </div>
            
            {/* Content */}
            <div className="p-4">
              <h4 className="font-bold text-lg mb-1 line-clamp-2">
                {formData.title || 'Your Idea Title'}
              </h4>
              <p className="text-xs text-stone-500 mb-3 capitalize">
                {formData.category} {formData.category === 'project' ? 'Project' : 'Recommendation'}
              </p>
              <p className="text-sm text-stone-600 mb-3 line-clamp-3">
                {formData.description || 'Your idea description will appear here...'}
              </p>
              
              {/* Tags Preview */}
              {formData.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-3">
                  {formData.tags.slice(0, 2).map((tag, index) => (
                    <span
                      key={index}
                      className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        tag.type === 'college' 
                          ? 'bg-violet-100 text-violet-600' 
                          : 'bg-stone-100 text-stone-600'
                      }`}
                    >
                      {tag.text}
                    </span>
                  ))}
                  {formData.tags.length > 2 && (
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-stone-100 text-stone-500">
                      +{formData.tags.length - 2}
                    </span>
                  )}
                </div>
              )}

            </div>
          </div>
          
          <p className="text-xs text-stone-500">
            This preview updates as you fill out the form
          </p>
        </div>
      </div>
    </div>
  );
} 